import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  Backdrop,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import colors from '../../../config/colors';
import http, { apiDmars } from '../../../config/http';
import { IntegraFormData } from './schema';
import { DependienteElegible, RelacionParentesco } from '../../state/queries/collections';
import {
  DocKey,
  DocumentFile,
  INITIAL_DOCS,
  INFO_DIAGNOSTICO,
  DocumentUploadItem,
} from './integra-document-upload-item';

const ID_TIPO_DOCUMENTO = 1; // CEDULA

// ─── Types ───────────────────────────────────────────────────────────────────

interface CreateSolicitudResponse {
  id: number;
  estado: string;
  creadoEn: string;
}

interface Step2Props {
  step1Data: IntegraFormData;
  parentescosOptions: RelacionParentesco[];
  infoAfiliado: any;
  dependienteSeleccionado: DependienteElegible;
  documentoIdentidad: string;
  onBack: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const Step2DocumentUpload: React.FC<Step2Props> = ({
  step1Data,
  parentescosOptions,
  infoAfiliado,
  dependienteSeleccionado,
  documentoIdentidad,
  onBack,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successCaseNumber, setSuccessCaseNumber] = useState<string | null>(null);
  const [docs, setDocs] = useState<Record<DocKey, DocumentFile>>(INITIAL_DOCS);

  const parentesco = parentescosOptions.find((p) => String(p.id) === step1Data.parentesco);
  const esTutorLegal = parentesco?.descripcion?.toLowerCase().includes('tutor') ?? false;
  const tieneTeaDiagnosticado = step1Data.tieneTeaDiagnosticado === 'si';
  const recibeTerapias = step1Data.recibeTerapias === 'si';

  const handleFileChange = (key: DocKey, file: File | null, error: string) => {
    setDocs((prev) => ({ ...prev, [key]: { file, error } }));
  };

  const validateDocs = (): Record<DocKey, DocumentFile> => {
    const newDocs = { ...docs };

    if (!newDocs.copiaCedulaTutor.file && !newDocs.copiaCedulaTutor.error) {
      newDocs.copiaCedulaTutor = { file: null, error: 'El documento Copia de Cédula es requerido' };
    }
    if (esTutorLegal && !newDocs.guardaCustodia.file && !newDocs.guardaCustodia.error) {
      newDocs.guardaCustodia = { file: null, error: 'El documento Guarda y Custodia es requerido' };
    }
    if (!newDocs.actaNacimiento.file && !newDocs.actaNacimiento.error) {
      newDocs.actaNacimiento = { file: null, error: 'El documento Acta de Nacimiento es requerido' };
    }
    if (tieneTeaDiagnosticado && !newDocs.diagnosticoMedico.file && !newDocs.diagnosticoMedico.error) {
      newDocs.diagnosticoMedico = { file: null, error: 'El documento Diagnóstico Médico Especializado es requerido' };
    }
    if (recibeTerapias && !newDocs.pruebasPsicometricas.file && !newDocs.pruebasPsicometricas.error) {
      newDocs.pruebasPsicometricas = {
        file: null,
        error: 'El documento Resultados de Pruebas Psicométricas es requerido',
      };
    }

    setDocs(newDocs);
    return newDocs;
  };

  const handleEnviar = () => {
    const validatedDocs = validateDocs();
    const docsConError = Object.values(validatedDocs).filter((d) => d.error);

    if (docsConError.length > 0) {
      const tieneErrorTamaño = docsConError.some((d) => d.error.toLowerCase().includes('tamaño'));
      const tieneRequeridos = docsConError.some((d) => d.error.toLowerCase().includes('requerido'));

      let mensaje: string;
      if (tieneErrorTamaño && tieneRequeridos) {
        mensaje =
          'Hay documentos que superan el tamaño permitido y documentos requeridos sin cargar. Elimina los archivos inválidos y completa los requeridos antes de continuar.';
      } else if (tieneErrorTamaño) {
        mensaje =
          'Hay documentos que superan el tamaño permitido. Usa el botón (×) para eliminarlos y carga uno nuevo antes de continuar.';
      } else {
        mensaje = 'Hay documentos requeridos sin cargar. Completa todos los campos requeridos antes de continuar.';
      }

      enqueueSnackbar(mensaje, { variant: 'error' });
      return;
    }

    setConfirmOpen(true);
  };

  const buildPayload = () => ({
    idAfiliadoDependiente: dependienteSeleccionado.idDependiente,
    perfilClinico: {
      tieneTeaDiagnosticado: step1Data.tieneTeaDiagnosticado === 'si',
      fechaDiagnostico: step1Data.fechaDiagnostico || null,
      recibeTerapias: step1Data.recibeTerapias === 'si',
      centroTerapia: step1Data.centroTerapia || null,
      terapiasEnCursoIdList: step1Data.recibeTerapiaIdList,
      idProfesionalDiagnostico:
        step1Data.idProfesionalDiagnostico !== '' ? Number(step1Data.idProfesionalDiagnostico) : null,
      otroProfesionalDiagnostico: step1Data.otroProfesionalDiagnostico || null,
      evaluacionesAplicadasIdList: step1Data.evaluacionesAplicadasIdList,
      comorbilidadIdList: step1Data.comorbilidadIdList,
      otraTerapia: step1Data.otraTerapia || null,
      otraEvaluacionAplicada: step1Data.otraEvaluacionAplicada || null,
      otraComorbilidad: step1Data.otraComorbilidad || null,
    },
    condicionEconomica: {
      idCondicionSocioeconomica: Number(step1Data.idCondicionSocioeconomica),
      otraSituacion: step1Data.otraSituacion || null,
      recibeSubsidio: step1Data.recibeSubsidio === 'si',
      tipoSubsidio: step1Data.tipoSubsidio || null,
    },
    direccion: {
      idProvincia: Number(step1Data.codigoProvincia) || null,
      idMunicipio: Number(step1Data.codigoMunicipio) || null,
      direccion: step1Data.direccion || null,
    },
    tutorLegal: {
      nombres: infoAfiliado.nombres,
      apellidos: infoAfiliado.apellidos,
      codigoAfiliado: infoAfiliado.codigoAfiliado,
      tipoDeDocumento: ID_TIPO_DOCUMENTO,
      numeroDeDocumento: documentoIdentidad,
      idParentesco: Number(step1Data.parentesco),
      estadoCivil: step1Data.estadoCivil || null,
      correoElectronico: step1Data.correoElectronico,
      correoElectronicoAlterno: step1Data.correoElectronicoAlterno || null,
      nivelEducativo: step1Data.nivelEducativo || null,
      situacionLaboral: step1Data.situacionLaboral || null,
      telefonoCelular: step1Data.celular || null,
      telefonoResidencial: step1Data.telefono || null,
    },
  });

  const handleConfirmSi = async () => {
    setConfirmOpen(false);
    setIsSubmitting(true);
    try {
      const solicitudPayload = buildPayload();
      console.log(solicitudPayload);
      const response = await apiDmars.post<CreateSolicitudResponse>('/solicitudes', solicitudPayload);
      const solicitudId = response.data.id;

      // y el registro del caso en CRM.
      const formDataTea = new FormData();
      formDataTea.append('idAfiliadoDependiente', dependienteSeleccionado.idDependiente.toString());
      formDataTea.append('idSolicitudDmars', solicitudId.toString());
      formDataTea.append(
        'tieneTeaDiagnosticado',
        solicitudPayload.perfilClinico.tieneTeaDiagnosticado ? 'true' : 'false'
      );
      formDataTea.append('recibeTerapias', solicitudPayload.perfilClinico.recibeTerapias ? 'true' : 'false');

      for (const element of Object.entries(docs)) {
        const [_, value] = element;
        if (value.file) {
          console.log(value.file);
          formDataTea.append('adjuntos', value.file);
        }
      }

      const {
        data: { numeroCasoCrm },
      } = await http.post<{ numeroCasoCrm: string }>('crm/solicitudes/SolicitudProgramaTea', formDataTea);

      setSuccessCaseNumber(numeroCasoCrm);
    } catch (e) {
      console.log(e);
      enqueueSnackbar('Ocurrió un error al enviar la solicitud. Intente nuevamente.', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nombreTutor = `${infoAfiliado.nombres} ${infoAfiliado.apellidos}`;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3, px: { xs: 2, md: 3 } }}>
      {/* Step indicator */}
      <Box sx={{ mb: 3 }}>
        <Typography variant='h5' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', color: colors.primary, mb: 0.5 }}>
          Formulario de Solicitud - Programa InTEgrA
        </Typography>
        <Typography variant='body2' sx={{ fontFamily: 'Montserrat', color: colors.gray }}>
          Paso 2 de 2 — Carga de Documentos
        </Typography>
      </Box>

      {/* Documentos del Tutor */}
      <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.grayLight}`, borderRadius: 2, overflow: 'hidden' }}>
        <Grid container>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ bgcolor: colors.grayUltraLight, p: 3, borderRight: { md: `1px solid ${colors.grayLight}` } }}
          >
            <Typography
              variant='subtitle2'
              sx={{ color: colors.primary, fontWeight: 'bold', fontFamily: 'Montserrat', mb: 0.5 }}
            >
              DOCUMENTOS DEL TUTOR
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', color: colors.gray, mb: 1 }}>
              {nombreTutor}
            </Typography>
            <Typography variant='body2' sx={{ color: colors.grayLight, fontFamily: 'Montserrat' }}>
              Documentos de identificación del padre, madre o tutor legal del beneficiario.
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} sx={{ p: 3 }}>
            <DocumentUploadItem
              label='Copia de Cédula'
              required
              docKey='copiaCedulaTutor'
              docs={docs}
              onFileChange={handleFileChange}
            />
            {esTutorLegal && (
              <DocumentUploadItem
                label='Documento de Guarda y Custodia'
                required
                docKey='guardaCustodia'
                docs={docs}
                onFileChange={handleFileChange}
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Documentos del Beneficiario */}
      <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.grayLight}`, borderRadius: 2, overflow: 'hidden' }}>
        <Grid container>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ bgcolor: colors.grayUltraLight, p: 3, borderRight: { md: `1px solid ${colors.grayLight}` } }}
          >
            <Typography
              variant='subtitle2'
              sx={{ color: colors.primary, fontWeight: 'bold', fontFamily: 'Montserrat', mb: 0.5 }}
            >
              DOCUMENTOS DEL BENEFICIARIO
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', color: colors.gray, mb: 1 }}>
              {dependienteSeleccionado.nombreCompleto}
            </Typography>
            <Typography variant='body2' sx={{ color: colors.grayLight, fontFamily: 'Montserrat' }}>
              Documentos clínicos y de identidad del afiliado beneficiario.
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} sx={{ p: 3 }}>
            <DocumentUploadItem
              label='Acta de Nacimiento'
              required
              docKey='actaNacimiento'
              docs={docs}
              onFileChange={handleFileChange}
            />
            {tieneTeaDiagnosticado && (
              <>
                <DocumentUploadItem
                  label='Diagnóstico Médico Especializado'
                  required
                  docKey='diagnosticoMedico'
                  docs={docs}
                  onFileChange={handleFileChange}
                  infoText={INFO_DIAGNOSTICO}
                />
                <DocumentUploadItem
                  label='Certificado de Discapacidad emitido por CONADIS'
                  required={false}
                  docKey='certificadoConadis'
                  docs={docs}
                  onFileChange={handleFileChange}
                />
              </>
            )}
            {recibeTerapias && !tieneTeaDiagnosticado && (
              <>
                <DocumentUploadItem
                  label='Diagnóstico Médico Especializado'
                  required
                  docKey='diagnosticoMedico'
                  docs={docs}
                  onFileChange={handleFileChange}
                  infoText={INFO_DIAGNOSTICO}
                />
                <DocumentUploadItem
                  label='Certificado de Discapacidad emitido por CONADIS'
                  required={false}
                  docKey='certificadoConadis'
                  docs={docs}
                  onFileChange={handleFileChange}
                />
              </>
            )}
            {recibeTerapias && (
              <>
                <DocumentUploadItem
                  label='Resultados de Pruebas Psicométricas'
                  required
                  docKey='pruebasPsicometricas'
                  docs={docs}
                  onFileChange={handleFileChange}
                />
                <DocumentUploadItem
                  label='Documentación de Apoyo'
                  required={false}
                  docKey='documentacionApoyo'
                  docs={docs}
                  onFileChange={handleFileChange}
                />
              </>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 4 }}>
        <Button
          variant='outlined'
          onClick={onBack}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            fontFamily: 'Montserrat',
            px: 4,
            borderColor: colors.grayLight,
            color: colors.gray,
          }}
        >
          Volver
        </Button>
        <Button
          variant='contained'
          onClick={handleEnviar}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            fontFamily: 'Montserrat',
            px: 4,
            bgcolor: colors.primary,
            '&:hover': { bgcolor: colors.primaryDark },
          }}
        >
          {isSubmitting ? <CircularProgress size={20} color='inherit' /> : 'Enviar'}
        </Button>
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontFamily: 'Montserrat', fontWeight: 'bold' }}>Confirmar envío</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ fontFamily: 'Montserrat' }}>
            ¿Estás seguro de que deseas realizar el envío de tu solicitud?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ textTransform: 'none', fontFamily: 'Montserrat', color: colors.gray }}
          >
            No
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmSi}
            sx={{
              textTransform: 'none',
              fontFamily: 'Montserrat',
              bgcolor: colors.primary,
              '&:hover': { bgcolor: colors.primaryDark },
            }}
          >
            Sí
          </Button>
        </DialogActions>
      </Dialog>
      {/* Success dialog */}
      <Dialog open={!!successCaseNumber} maxWidth='xs' fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ fontFamily: 'Montserrat', fontWeight: 'bold' }}>¡Solicitud Enviada!</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ fontFamily: 'Montserrat', mb: 1 }}>
            Su solicitud fue creada con éxito. Estará siendo atendida bajo el caso:
          </Typography>
          <Typography
            variant='h6'
            sx={{ fontFamily: 'Montserrat', fontWeight: 'bold', textAlign: 'center', color: colors.primary }}
          >
            #{successCaseNumber}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => navigate('/')}
            sx={{ textTransform: 'none', fontFamily: 'Montserrat', color: colors.gray }}
          >
            Aceptar
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              navigator.clipboard.writeText(successCaseNumber!);
              navigate('/');
            }}
            sx={{
              textTransform: 'none',
              fontFamily: 'Montserrat',
              bgcolor: colors.primary,
              '&:hover': { bgcolor: colors.primaryDark },
            }}
          >
            Copiar y cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={isSubmitting}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </Box>
  );
};
